/**
 * Paginated menu view.
 * pass the collection and the 'next_url' in the options on instantiation:
 *
 *  var menu = new PaginatedMenu({
 *      collection: collection,
 *      next_url: 'https://build.ie.run/v5/products?page[number]=2'
 *      title: 'Choose a Product'
 *  });
 *
 * Listen for the 'select' event to get the model that was selected:
 *
 *  menu.on('select', function(model) {
 *      console.log("%s was selected", model.get('name'));
 *  }
 */
import _ from 'underscore';
import Backbone from 'backbone';
import Marionette from 'backbone.marionette';
import Handlebars from 'handlebars';
import template from '@/../templates/paginated-menu.js';
    var PaginatedMenu = Marionette.CompositeView.extend({
        template: Handlebars.compile(template),
        initialize: function(options) {
            // Set up a proxy collection. Doing the filter on a large
            // collection is madness. We'll use this internal collection to
            // manage the items for the view.
            var filter = this.getOption('filter');
            if (!filter) {
                filter = function() { return true; };
            }
            var proxy_collection =
                new Backbone.Collection(this.collection.filter(filter));
            // retain a handle to the canonical collection passed to the view
            this._collection = this.collection;
            this.collection = proxy_collection;

            // You can pass in next and prev url
            this.links = {
                next: this.getOption('next_url'),
                prev: this.getOption('prev_url')
            };
            _.bindAll(this, 'update_links_from_fetch');
        },
        ui: {
            toggle: '.dropdown-toggle',
            title: '.menu-title',
            menu_info: '.menu-info',
            actions: '.actions',
            next: '.actions .next',
            items: '.dropdown .children',
            title_text: '.menu-title .text',
        },
        events: {
            'click @ui.next': 'fetch_next',
            'click @ui.title': 'handle_title_click'
        },
        templateContext: function () {
            var custom_attributes = this.getOption('custom_attributes');
            return {
                custom_attributes: custom_attributes,
            };
        },
        childViewEvents: {
            'select': 'handle_selected_child'
        },
        childView: Marionette.View,
        childViewContainer: '.children',
        childViewOptions: {
            tagName: 'li',
            attributes: function() {
                return {
                    'data-id': this.model.id,
                    'data-type': this.model.type
                };
            },
            template: Handlebars.compile('<a href="{{url}}">{{username}}{{name}}</a>'),
            templateContext: function() {
                let url = '';
                let is_draft = false;

                if (this.model) {
                    switch ( this.model.type ) {
                        case 'product':
                            url = '/ide/products/' + this.model.id +'/devicegroups/' + this.model.menu_action;
                            break;
                        case 'account':
                            url = '/ide/' + this.model.get('username') + '/products/' + this.model.id;
                            break;
                        case 'view_all_pre_production_devicegroups':
                            url = '/ide/products' + this.model.rel.get('product').id + '/test_production';
                            break;
                        case 'view_all_pre_factoryfixture_devicegroups':
                            url = '/ide/products' + this.model.rel.get('product').id + '/test_factory';
                            break;
                        case 'view_all_pre_dut_devicegroups':
                            url = '/ide/products' + this.model.rel.get('product').id + '/test_dut';
                            break;
                        case 'draft':
                            url = '/ide/devicegroups' + this.model.id + '/draft';
                            is_draft = true;
                            break;
                        default:
                            url = '/ide/devicegroups/' + this.model.id;
                    }
                }

                return {
                    url: url,
                    is_draft: is_draft
                };
            },
            events: {
                // When a child item is selected, trigger a 'select' event
                // that the parent can listen for, passing the corresponding
                // model.
                'click': function(e) {
                    e.preventDefault();
                    if ( e.which === 1 ) {
                        this.trigger('select', this.model, e);
                        return false;
                    }
                }
            }
        },
        set_selected_child: function(id) {
            this.selected_child = this.collection.get(id);
        },
        handle_selected_child: function(child_view_model, e) {
            e.preventDefault();
            if ( e.which === 1) {
                if (child_view_model.type === 'account') {
                    this.set_menu_title(child_view_model.get('username'));
                } else {
                    this.set_menu_title(child_view_model.get('name'));
                }
                this.set_selected_child(child_view_model.id);
                this.trigger('select', child_view_model);
                return false;
            }
        },
        handle_title_click: function(e) {
            if ( this.getOption('no_link') ) return;
            if ( e.which === 1 ) {
                this.trigger('select', this.selected_child);
                return false;
            }
        },
        set_menu_title: function(title) {
            try {
                if (_.isFunction(this.ui.title_text.text) ) {
                    this.ui.title_text.text(title); 
                } else {
                    this.ui.title_text.text = title;
                }
            } catch (e) {
                $('.menu-title .text').text(title);
            }
            if ( this.model ) {
                switch ( this.model.type ) {
                    case 'account':
                        this.ui.title_text.attr('href', '/ide/'+title+'/products');
                        break;
                    case 'product':
                        this.ui.title_text.attr('href', '/ide/products/'+this.model.id);
                        break;
                    default:
                        this.ui.title_text.attr('href', '/ide/devicegroup/'+this.model.id);
                }
            }
        },
        set_menu_info: function(info) {
            if ( _.isFunction(this.ui.menu_info.text) ) {
                this.ui.menu_info.text(info);
            } else {
                this.ui.menu_info.text = info;
            }
        },
        fetch: function(url) {
            var collection = this.collection;
            var options = {
                remove: false,
                success: function(coll, resp, options) {
                    collection.add(coll.filter(options.filter));
                }
            };
            options.url = url || _.result(this._collection, 'url');
            this._collection.fetch(options).then(this.update_links_from_fetch);
        },
        fetch_next: function(e) {
            e.preventDefault();
            e.stopPropagation();
            this.fetch(this.links.next);
        },
        update_links_from_fetch: function(resp) {
            var links = this.links;
            if (!resp.links) {
                this.next_url = null;
                this.prev_url = null;
                return;
            }
            _(['next', 'prev']).each(function(key) {
                if (resp.links[key]) {
                    links[key] = resp.links[key];
                } else {
                    links[key] = null;
                }
            });
            this.show_view_more();
        },
        /**
         * Disable or enable the dropdown-toggle
         * @param boolean Disable or not. if undefined, assume true
         */
        disable: function(disable) {
            if (_.isUndefined(disable)) { disable = true; }
            if (disable) {
                this.ui.toggle.addClass('disabled');
                this.ui.toggle.prop('disabled', true);
            } else {
                this.ui.toggle.removeClass('disabled');
                this.ui.toggle.prop('disabled', false);
            }
        },
        show_view_more: function() {
            if (this.links.next) {
                this.ui.next.show();
            } else {
                this.ui.next.hide();
            }
        },
        onRender: function() {
            this.show_view_more();
            if (this.options.disabled) this.disable();
        },
        onAddChild: function(child) {
            // Get the view more link and re-append it to the end
            // of the child list. We have to do this because marionette wants
            // one element as the childViewContainer, which because of
            // bootstrap must be a UL element, which can only contain LIs.
            // It would be nice to set aside a region within the boostrap
            // dropdown that we could permanently have the View More link, but
            // it just doesn't work that way. We have to dynamincally add the
            // View More link to the bottom whenever more elements are added
            // to the menu.
            this.ui.actions.detach().appendTo(this.ui.items);
            // If there are tooltips to be shown
            child.$el.find('[data-toggle="tooltip"]').tooltip();
        },
        onAttach: function() {
            this.ui.actions.detach().appendTo(this.ui.items);
            this.show_view_more();
            var title = this.getOption('title');
            if (title) {
                this.set_menu_title(title);
            }
        }
    });
    export default PaginatedMenu;

